/**
 * The study consent form (NTU-IRB Ref No. IRB-2025-996), adapted for this game.
 *
 * The text below is UI copy, not tuning: edit it here. Its structure follows
 * the consent form the same team used in Craft Together — the same
 * researchers, contacts and sections — with the task, the duration and the
 * recording notice rewritten for a two-player cooking game with live voice.
 * One phrase is the study's rather than the code's: how long the data is
 * kept, `consent.retention` in `study.json`, which arrives with the config.
 *
 * The box can only be ticked once the text has been scrolled to the bottom
 * (or fits without scrolling), and "Consent and continue" needs the box.
 * "Leave the study" is a decline, and ends the session at stage 0.
 *
 * Bumping the wording means bumping `STUDY_CONSENT_VERSION` in the shared
 * package; the friends-mode `CONSENT_VERSION` is never touched by this file.
 */

import { useEffect, useRef, useState } from 'react'

interface StudyConsentProps {
  /** How long the data is kept, as a phrase: "kept until …, then deleted". */
  retention: string
  onAccept(): void
  onDecline(): void
}

/** How close to the bottom counts as having scrolled to it. */
const BOTTOM_SLACK_PX = 20

export function StudyConsent({ retention, onAccept, onDecline }: StudyConsentProps) {
  const [readToEnd, setReadToEnd] = useState(false)
  const [ticked, setTicked] = useState(false)
  const [answered, setAnswered] = useState<'accepted' | 'declined' | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Text that fits without scrolling has been read to the end by definition.
  useEffect(() => {
    const el = scrollRef.current
    if (el && el.scrollHeight <= el.clientHeight + BOTTOM_SLACK_PX) setReadToEnd(true)
  }, [])

  const onScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const el = event.currentTarget
    if (el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_SLACK_PX) setReadToEnd(true)
  }

  if (answered) {
    return (
      <section className="gate" role="status">
        <h1>{answered === 'accepted' ? 'Thank you' : 'You chose not to take part'}</h1>
        <p className="hint">One moment…</p>
      </section>
    )
  }

  return (
    <section className="gate gate-consent study-consent" aria-labelledby="study-consent-title">
      <p className="gate-step">Consent form</p>
      <h1 id="study-consent-title">Understanding Human Communication and Collaboration</h1>
      <p className="hint">
        NTU-IRB Ref No.: <code>IRB-2025-996</code>
      </p>

      <div className="study-consent-text" ref={scrollRef} onScroll={onScroll} tabIndex={0} data-testid="study-consent-text">
        <p>
          You are invited to participate in a research study on how people communicate and
          collaborate when solving tasks together. This study is conducted by <b>Dr. Yewen Pu</b>,
          Assistant Professor, School of Computer Science and Engineering, Nanyang
          Technological University, and <b>Abhipray Chavan</b>, researcher.
        </p>

        <h3>What you will do</h3>
        <p>
          You will play a two-player cooperative cooking game in your web browser with another
          participant, talking to each other by live voice. You will each control a chef in a
          shared kitchen and work together to prepare and serve dishes.
        </p>

        <h3>Duration</h3>
        <p>
          The study takes about <b>30 minutes</b> in total: a short setup and microphone check, an
          interactive tutorial, a short wait while we pair you with a partner, <b>three game
          rounds of 5 minutes each</b>, and a short survey.
        </p>

        <h3>Audio Recording Notice</h3>
        <p>
          During the three game rounds we will record your voice, through your microphone, as
          you talk with your partner, along with your moves in the game. Your partner's
          microphone records their side in the same way. No audio is recorded during this
          consent screen, the tutorial, the microphone check, the waiting room or the survey.
          Recordings may be transcribed for analysis. Please do not say your name or other
          identifying details to your partner. By consenting, you specifically consent to the
          audio recording described here.
        </p>

        <h3>How long we keep your data</h3>
        <p data-testid="study-consent-retention">
          Your recordings, game logs and survey answers are <b>{retention.trim().replace(/\.$/, '')}</b>.
        </p>

        <h3>Voluntary participation and withdrawal</h3>
        <p>
          Your participation in this study is completely voluntary, and you have the right to
          withdraw from the study at any time without any penalty. You may skip any survey
          question you do not wish to answer where it is marked optional. If you do not wish to
          continue, you can leave the study, or simply close your browser.
        </p>
        <p data-testid="study-consent-withdrawal">
          You can also withdraw after you finish, by sending the research team a message
          through Prolific. If you withdraw, we <b>delete your voice recordings, your survey
          answers and the record of your progress through the study</b>. Your partner's own
          recordings are not affected. We keep only your Prolific ID, your submission, how far
          you got and how long it took, so that we can still pay you for your time; your moves
          stay in the game logs under a random ID that is no longer linked to you.
        </p>

        <h3>Confidentiality</h3>
        <p>
          Your participation in this research will be kept confidential. Research data is stored
          in <b>coded (pseudonymous)</b> form: each session is identified by a randomly generated
          internal participant ID rather than your name or other directly identifying details.
          Your Prolific ID is held separately, only for compensation and duplicate-participation
          checks, and is <b>not</b> included in any publications, shared datasets or analysis
          files released outside the research team. Results will be analysed in aggregate and may
          be disseminated through academic publications, conference presentations and open
          research datasets.
        </p>

        <h3>Storage Location</h3>
        <p>
          Session data (audio, game logs and survey responses) is stored on secure cloud object
          storage located in the United States. For participants in the European Union / EEA, the
          transfer relies on standard contractual safeguards and, where applicable, the EU-US Data
          Privacy Framework.
        </p>

        <h3>Compensation</h3>
        <p>
          You will be compensated for your time through Prolific according to the platform's
          payment rules. There are no
          foreseeable risks to individuals participating in this study beyond those encountered
          in ordinary online activities.
        </p>

        <h3>Contact</h3>
        <p>
          If you have questions about this project, please contact <b>Abhipray Chavan</b> at{' '}
          <a href="mailto:abhipray001@e.ntu.edu.sg">abhipray001@e.ntu.edu.sg</a> (research team)
          or <b>Dr. Yewen Pu</b> at <a href="mailto:yewen.pu@ntu.edu.sg">yewen.pu@ntu.edu.sg</a>.
        </p>
        <p>
          This project has been reviewed and approved by the <b>NTU Institutional Review Board
          (NTU-IRB)</b>. Questions about your rights as a participant may be directed to{' '}
          <a href="mailto:IRB@ntu.edu.sg">IRB@ntu.edu.sg</a> or by calling <b>+65 6592 2495</b>.
        </p>

        <p className="study-consent-certify">
          I have read and understood the above consent form. I certify that I am <b>21 years old
          or older</b> and, by clicking "Consent and continue" below, I indicate my willingness to
          voluntarily take part in the research.
        </p>
      </div>

      {!readToEnd && <p className="hint">Scroll to the end of the form to continue.</p>}

      <label className="consent-check">
        <input
          type="checkbox"
          checked={ticked}
          disabled={!readToEnd}
          onChange={(event) => setTicked(event.target.checked)}
          data-testid="study-consent-tick"
        />
        I have read and understood the consent form, I am 21 or older, and I agree to take part.
      </label>

      <div className="gate-actions">
        <button
          type="button"
          className="consent-agree"
          disabled={!ticked}
          onClick={() => {
            setAnswered('accepted')
            onAccept()
          }}
          data-testid="study-consent-agree"
        >
          Consent and continue
        </button>
        <button
          type="button"
          onClick={() => {
            setAnswered('declined')
            onDecline()
          }}
          data-testid="study-consent-decline"
        >
          Leave the study
        </button>
      </div>
    </section>
  )
}
